import React, { useMemo } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import {
  selectCrossClientFinishedResultsAtAiBatchStart,
  selectCrossClientSucceededResultsAtLocalResolverStart,
  selectCrossClientProgressStatuses,
  selectCrossClientResultsExecution,
} from '../../../../store/crossClient/crossClientResults'
import {
  CrossClientQueryAiEvaluationModes,
  CrossClientQueryPhases,
  CrossClientQueryResultStatuses,
  type CrossClientQueryExecution,
  type CrossClientQueryPhase,
  type CrossClientQueryResultStatus,
} from '../../../../models/CrossClient'
import { ReactComponent as ZocksLogo } from '../../../../assets/ZocksLogo.svg'
import { ReactComponent as StepCompletedIcon } from '../../../../assets/images/cross-client-step-completed.svg'
import { ReactComponent as StepAiOffIcon } from '../../../../assets/images/cross-client-steps-ai-off.svg'
import { ReactComponent as StepAiOnIcon } from '../../../../assets/images/cross-client-steps-ai-on.svg'
import { getIntegrationSourceDisplayNames, joinNames } from '../../utils'

type StepStatus = 'done' | 'active' | 'pending'

interface StepView {
  key: 'bulk' | 'local' | 'ai'
  status: StepStatus
  title: React.ReactNode
  description?: React.ReactNode
  progress?: { numerator: number; denominator: number }
  icon?: 'ai-on' | 'ai-off'
}

interface CrossClientSidebarProgressProps {
  footer?: React.ReactNode
}

export const CrossClientSidebarProgress: React.FC<CrossClientSidebarProgressProps> = ({ footer }) => {
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const progress = useAppSelector(selectCrossClientProgressStatuses)
  const succeededResultsAtLocalResolverStart = useAppSelector(selectCrossClientSucceededResultsAtLocalResolverStart)
  const finishedResultsAtAiBatchStart = useAppSelector(selectCrossClientFinishedResultsAtAiBatchStart)

  const steps = useMemo(
    () => (execution ? buildSteps(execution, progress, succeededResultsAtLocalResolverStart, finishedResultsAtAiBatchStart) : []),
    [execution, progress, succeededResultsAtLocalResolverStart, finishedResultsAtAiBatchStart],
  )

  if (!execution || steps.length === 0) {
    return null
  }

  const completedCopy = getCompletedCopy(execution.phase)

  return (
    <div className='d-flex flex-column gap-3 px-3 py-4 border border-1 zocks-border-color-gray-200 zocks-border-1.0 bg-white'>
      <ol className='cross-client-sidebar-progress__steps'>
        {steps.map((step, index) => (
          <SidebarStep key={step.key} step={step} showConnector={index < steps.length - 1} />
        ))}
      </ol>
      {completedCopy ? <div className='paragraph-medium medium'>{completedCopy}</div> : null}
      {footer}
    </div>
  )
}

const SidebarStep: React.FC<{ step: StepView; showConnector: boolean }> = ({ step, showConnector }) => {
  return (
    <li className={`cross-client-sidebar-progress__step cross-client-sidebar-progress__step--${step.status}`}>
      <div className='cross-client-sidebar-progress__rail'>
        {step.icon === 'ai-on' ? (
          <StepAiOnIcon width={20} height={20} className='cross-client-sidebar-progress__bullet' />
        ) : step.icon === 'ai-off' ? (
          <StepAiOffIcon width={20} height={20} className='cross-client-sidebar-progress__bullet' />
        ) : step.status === 'active' ? (
          <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25 cross-client-sidebar-progress__bullet cross-client-sidebar-progress__bullet--active'>
            <ZocksLogo width={12} height={12} />
          </div>
        ) : step.status === 'done' ? (
          <StepCompletedIcon width={16} height={16} className='cross-client-sidebar-progress__bullet cross-client-sidebar-progress__bullet--done' />
        ) : (
          <div className='cross-client-sidebar-progress__bullet cross-client-sidebar-progress__bullet--pending' aria-hidden='true' />
        )}
        {showConnector ? <div className='cross-client-sidebar-progress__connector' /> : null}
      </div>
      <div className='d-flex flex-column gap-2 cross-client-sidebar-progress__body'>
        <div className={`paragraph-medium mb-0 ${step.status === 'pending' ? 'zocks-text-gray-600' : ''}`}>{step.title}</div>
        {step.progress ? <ProgressBar numerator={step.progress.numerator} denominator={step.progress.denominator} /> : null}
        {step.description ? <div className='paragraph-small zocks-text-gray-900'>{step.description}</div> : null}
      </div>
    </li>
  )
}

const ProgressBar: React.FC<{ numerator: number; denominator: number }> = ({ numerator, denominator }) => {
  const percentage = denominator > 0 ? Math.min(100, Math.max(0, Math.floor((numerator / denominator) * 100))) : 0
  return (
    <div className='cross-client-sidebar-progress__progress-track' role='progressbar' aria-valuenow={percentage} aria-valuemin={0} aria-valuemax={100}>
      <div className='cross-client-sidebar-progress__progress-fill' style={{ width: `${percentage}%` }} />
    </div>
  )
}

const buildSteps = (
  execution: CrossClientQueryExecution,
  progress: Partial<Record<CrossClientQueryResultStatus, number>>,
  succeededResultsAtLocalResolverStart: number,
  finishedResultsAtAiBatchStart: number,
): StepView[] => {
  const { phase } = execution

  const succeeded = progress[CrossClientQueryResultStatuses.SUCCEEDED] ?? 0
  const finished = succeeded + (progress[CrossClientQueryResultStatuses.NO_DATA] ?? 0) + (progress[CrossClientQueryResultStatuses.FAILED] ?? 0)
  const aiPending = progress[CrossClientQueryResultStatuses.AI_PENDING] ?? 0
  const localResolverResultCount = Math.max(execution.resultCount - succeededResultsAtLocalResolverStart, 0)
  const localResolverResolvedResultCount = Math.max(succeeded - succeededResultsAtLocalResolverStart, 0) + aiPending
  const remainingForAi = (progress[CrossClientQueryResultStatuses.INITIALIZED] ?? 0) + aiPending

  const bulkDone =
    phase !== CrossClientQueryPhases.INITIALIZED &&
    phase !== CrossClientQueryPhases.NO_CLIENTS_WERE_FOUND &&
    phase !== CrossClientQueryPhases.NO_CLIENTS_AFTER_FILTER_IDS &&
    phase !== CrossClientQueryPhases.NO_CLIENTS_AFTER_FILTER_ROLE &&
    phase !== CrossClientQueryPhases.QUESTION_ERROR

  const localActive = phase === CrossClientQueryPhases.LOCAL_QUEUED || phase === CrossClientQueryPhases.LOCAL_PROCESSING
  const localDone =
    phase === CrossClientQueryPhases.AI_READY ||
    phase === CrossClientQueryPhases.AI_QUEUED ||
    phase === CrossClientQueryPhases.AI_PROCESSING ||
    phase === CrossClientQueryPhases.AI_STOPPED ||
    phase === CrossClientQueryPhases.COMPLETED

  const aiActive = phase === CrossClientQueryPhases.AI_QUEUED || phase === CrossClientQueryPhases.AI_PROCESSING
  const aiReady = phase === CrossClientQueryPhases.AI_READY || phase === CrossClientQueryPhases.AI_STOPPED
  const aiDone = phase === CrossClientQueryPhases.COMPLETED

  const bulkStep: StepView = {
    key: 'bulk',
    status: bulkDone ? 'done' : 'active',
    title: 'Client Profiles in Zocks checked',
  }

  const integrationNames = getIntegrationSourceDisplayNames(execution.sources)
  const integrationsList = joinNames(integrationNames)
  const localDoneTitle = integrationsList ? `${integrationsList} data checked` : 'External sources data checked'

  const localStep: StepView = localActive
    ? {
        key: 'local',
        status: 'active',
        title: (
          <strong>
            Checking {Math.min(localResolverResolvedResultCount + 1, Math.max(localResolverResultCount, 1)).toLocaleString()} of{' '}
            {localResolverResultCount.toLocaleString()} clients{' '}
            {integrationsList ? `by pulling additional data from ${integrationsList}` : 'by pulling additional data from your external sources'}
          </strong>
        ),
        description: "Results update as I go. Close this page if you need to — I'll notify you when done.",
        progress: { numerator: localResolverResolvedResultCount, denominator: localResolverResultCount },
      }
    : localDone
      ? { key: 'local', status: 'done', title: localDoneTitle }
      : { key: 'local', status: 'pending', title: localDoneTitle }

  const aiStep: StepView = (() => {
    const aiEvaluationResultCount = Math.max(execution.resultCount - finishedResultsAtAiBatchStart, 0)
    const aiBatchTotal =
      execution.aiEvaluationMode === CrossClientQueryAiEvaluationModes.STEPPED && execution.aiBatchSize
        ? Math.min(execution.aiBatchSize, aiEvaluationResultCount)
        : aiEvaluationResultCount
    const aiInBatch = Math.max(finished - finishedResultsAtAiBatchStart, 0)
    if (aiActive && aiEvaluationResultCount > 0) {
      return {
        key: 'ai',
        status: 'active',
        title: (
          <strong>
            Running deeper check on {Math.min(aiInBatch + 1, aiBatchTotal).toLocaleString()} of {aiBatchTotal.toLocaleString()} clients
          </strong>
        ),
        description: "Results update as I go. Close this page if you need to — I'll notify you when done.",
        progress: { numerator: aiInBatch, denominator: aiBatchTotal },
      }
    }
    if (aiReady) {
      return {
        key: 'ai',
        status: 'active',
        icon: 'ai-on',
        title: (
          <>
            <strong>
              {remainingForAi.toLocaleString()} {remainingForAi === 1 ? 'client is' : 'clients are'}
            </strong>{' '}
            still unconfirmed. Run a deeper check for more context.
          </>
        ),
      }
    }
    if (aiDone) {
      return { key: 'ai', status: 'done', title: 'Deeper check complete' }
    }
    return {
      key: 'ai',
      status: 'pending',
      icon: 'ai-off',
      title: "For clients that couldn't be confirmed, you can run a deeper check for more context.",
    }
  })()

  const hasBulk = execution.resolvableClauseVariables == null || execution.resolvableClauseVariables.length > 0
  const hasLocal = execution.localResolvableClauseVariables == null || execution.localResolvableClauseVariables.length > 0

  return [...(hasBulk ? [bulkStep] : []), ...(hasLocal ? [localStep] : []), aiStep]
}

const getCompletedCopy = (phase: CrossClientQueryPhase): React.ReactNode => {
  if (phase === CrossClientQueryPhases.COMPLETED) {
    return 'All clients checked. The query is complete.'
  }
  if (phase === CrossClientQueryPhases.CANCELED) {
    return 'This query was stopped before all clients were checked.'
  }
  if (phase === CrossClientQueryPhases.ERRORED) {
    return 'Something went wrong before all clients could be checked.'
  }
  return null
}
